import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import useSWRImmutable from 'swr/immutable';
import { getZonesFromAPI } from '/@/store/public-data/apiProviders';
import { getSessionCountry } from '/@/store/session/selectors';
import { getCurrentCampaignYearFromFallowZone } from '/@/utils/date';

export const useZones = (
    {
        lat,
        lng,
        type,
        plantingDate,
    }: {
        lat?: number;
        lng?: number;
        type?: string;
        plantingDate?: string;
    },
    { enabled = true }: { enabled?: boolean } = { enabled: true }
) => {
    let key = null;
    if (enabled) key = { lat, lng, type, plantingDate, key: 'useZones' };

    return useSWRImmutable(key, getZonesFromAPI);
};

/**
 * Hook to get the current campaign year based on fallow zone
 * Uses parseYearlessDate logic to determine the campaign year from the fallow zone's min_date
 * Returns the campaign year for the current date based on the fallow zone logic
 */
export const useCurrentCampaignYear = (referenceDate?: Date): number | null => {
    const country = useSelector(getSessionCountry);

    // Fetch fallow zones (without lat/lng to get all zones, then filter by country)
    const fallowZonesQuery = useZones({ type: 'fallow' }, { enabled: !!country?.code });

    const currentYear = useMemo(() => {
        if (!fallowZonesQuery.data || !Array.isArray(fallowZonesQuery.data) || !country?.code) {
            return null;
        }

        // Filter fallow zones by country code (zones have codename that starts with country code)
        const countryFallowZone = fallowZonesQuery.data.find((zone) => zone.codename?.startsWith(country.code));

        if (!countryFallowZone?.minDate) {
            return null;
        }

        return getCurrentCampaignYearFromFallowZone(countryFallowZone.minDate, referenceDate);
    }, [fallowZonesQuery.data, country?.code, referenceDate]);

    return currentYear;
};
